import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import KEYBOARD_SHORTCUTS, { TShortcutName } from '../../constants/shortcuts'
import { useKeyboardShortcut } from '../../hooks'
import useCanHover from '../../hooks/useCanHover'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { Border, Colors, Dimensions, Spacing, Typography } from '../../styles'
import { pointerQuery } from '../../styles/dimensions'
import { icons } from '../../styles/images'
import { READ_ONLY_MESSAGE, notifyReadOnlyRejection } from '../../utils/readOnly'
import { stopKeydownPropogation } from '../../utils/utils'
import { Icon } from '../atoms/Icon'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import Tip from '../radix/Tip'

export const CreateNewItemInputContainer = styled.div`
    display: flex;
    flex-shrink: 0;
    flex-direction: row;
    gap: ${Spacing._8};
    background-color: ${Colors.background.white};
    height: ${Dimensions.TASK_HEIGHT};
    ${pointerQuery.noHover} {
        height: ${Dimensions.TASK_HEIGHT_TOUCH};
    }
    align-items: center;
    padding: 0px ${Spacing._8};
    border-radius: ${Border.radius.medium};
    box-sizing: border-box;
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    :focus-within {
        border: ${Border.stroke.medium} solid ${Colors.text.ink};
    }
    margin-bottom: ${Spacing._16};
    transition: border var(--animate-border-easing);
`
export const TaskInput = styled.input`
    border: none;
    outline: none;
    background-color: transparent;
    flex: 1;
    min-width: 0;
    ${Typography.body.medium};
    ${Typography.touchFieldFloor};
`
const SubmitButton = styled(NoStyleButton)`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 ${Spacing._44};
    width: ${Spacing._44};
    height: ${Spacing._44};
    border-radius: 50%;
    color: ${Colors.control.primary.label};
    background-color: ${Colors.control.primary.bg};
    border: ${Border.stroke.medium} solid ${Colors.control.primary.stroke};

    &:active:enabled,
    &:focus:enabled {
        background-color: ${Colors.control.primary.highlight};
    }

    &:disabled {
        opacity: 0.35;
    }
`

const blurShortcuts = [KEYBOARD_SHORTCUTS.close.key]

interface CreateNewItemInputProps {
    initialValue?: string
    placeholder: string
    shortcutName?: TShortcutName
    autoFocus?: boolean
    hideIcon?: boolean
    submitLabel?: string
    isDefaultTaskCaptureInput?: boolean
    onChange?: (text: string) => void
    /** `restore` puts the submitted text back, for a caller whose create failed; it never overwrites newer typing. */
    onSubmit?: (text: string, restore: () => void) => void
    onBlur?: () => void
}
const CreateNewItemInput = ({
    initialValue = '',
    placeholder,
    shortcutName,
    autoFocus,
    hideIcon,
    submitLabel,
    isDefaultTaskCaptureInput,
    onChange,
    onSubmit,
    onBlur,
}: CreateNewItemInputProps) => {
    const [text, setText] = useState(initialValue)
    const [shouldFocus, setShouldFocus] = useState(autoFocus ?? false)
    const inputRef = useRef<HTMLInputElement>(null)
    const canHover = useCanHover()
    const isReadOnly = useIsReadOnly()

    const focusInput = useCallback(() => {
        inputRef.current?.focus()
    }, [])
    const keepInputFocused = useCallback(() => {
        focusInput()
        if (window.requestAnimationFrame) {
            window.requestAnimationFrame(focusInput)
        } else {
            window.setTimeout(focusInput, 0)
        }
    }, [focusInput])

    const submitText = useCallback(() => {
        if (isReadOnly) return notifyReadOnlyRejection()
        keepInputFocused()
        const trimmedText = text.trim()
        if (!onSubmit || trimmedText === '') return
        setText('')
        onSubmit(trimmedText, () => setText((current) => (current === '' ? trimmedText : current)))
        setShouldFocus(true)
    }, [isReadOnly, keepInputFocused, onSubmit, text])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        stopKeydownPropogation(e, blurShortcuts)
        if (blurShortcuts.includes(e.key)) {
            inputRef.current?.blur()
        }
        if (e.key === 'Enter') {
            e.preventDefault()
            submitText()
        }
    }

    useEffect(() => {
        if (shouldFocus) {
            keepInputFocused()
            setShouldFocus(false)
        }
    }, [keepInputFocused, shouldFocus])

    useKeyboardShortcut(
        shortcutName,
        useCallback(() => setShouldFocus(true), []),
        !shortcutName
    )

    return (
        <Tip shortcutName={shortcutName} side="top" disabled={!shortcutName}>
            <CreateNewItemInputContainer>
                {!hideIcon && <Icon icon={icons.plus} />}
                <TaskInput
                    ref={inputRef}
                    value={text}
                    placeholder={isReadOnly ? READ_ONLY_MESSAGE : placeholder}
                    readOnly={isReadOnly}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => {
                        setText(e.target.value)
                        onChange?.(e.target.value)
                    }}
                    autoFocus={autoFocus}
                    data-autofocus={autoFocus}
                    data-default-task-capture-input={isDefaultTaskCaptureInput ? '' : undefined}
                    onBlur={onBlur}
                />
                {!canHover && (
                    <SubmitButton
                        aria-label={submitLabel ?? placeholder}
                        disabled={isReadOnly || text.trim() === ''}
                        onPointerDown={(e) => {
                            e.preventDefault()
                            focusInput()
                        }}
                        onClick={submitText}
                    >
                        <Icon icon={icons.arrow_up} />
                    </SubmitButton>
                )}
            </CreateNewItemInputContainer>
        </Tip>
    )
}

export default CreateNewItemInput
